import React, { useState, useEffect } from "react";
import axios from "axios";
import TopHeader from "../../../components/dashboard/user-dashboard/TopHeader";
import loader from "../../../assets/images/loader.gif";

const AdminAnalytics = () => {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Metrics states
  const [activeUsers, setActiveUsers] = useState(null);
  const [newUsers, setNewUsers] = useState(null);
  const [avgEngagement, setAvgEngagement] = useState(null);
  const [eventCount, setEventCount] = useState(null);

  // Tables states
  const [topPages, setTopPages] = useState([]);
  const [trafficSources, setTrafficSources] = useState([]);
  const [userTrend, setUserTrend] = useState({ labels: [], datasets: [] });

  // Hover states for interactive SVG charts
  const [hoveredTrendIndex, setHoveredTrendIndex] = useState(null);
  const [hoveredDonutIndex, setHoveredDonutIndex] = useState(null);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const colorPalette = ["#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444", "#6b7280"];

  useEffect(() => {
    fetchAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("accessToken");
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      // Helper function to safely fetch individual reports
      const fetchReportSafe = async (reportType, fallback) => {
        try {
          const response = await axios.get(
            `${BASE_URL}admin/analytics/reports/${reportType}?startDate=${startDate}&endDate=${endDate}`,
            { headers }
          );
          if (response.data?.header?.code === 200 && response.data.body?.value) {
            return response.data.body.value.data;
          }
          console.warn(`Report ${reportType} returned code ${response.data?.header?.code}:`, response.data?.body?.error);
          return fallback;
        } catch (err) {
          console.error(`Failed to load ${reportType} report:`, err);
          return fallback;
        }
      };

      // Run reports in parallel safely
      const [
        activeUsersData,
        newUsersData,
        avgEngagementData,
        eventCountData,
        topPagesData,
        sourcesData,
        trendData,
      ] = await Promise.all([
        fetchReportSafe("activeUsers", { formattedValue: "0", value: "0", unit: "users" }),
        fetchReportSafe("newUsers", { formattedValue: "0", value: "0", unit: "users" }),
        fetchReportSafe("averageEngagementTime", { formattedValue: "0", value: "0", unit: "seconds per user" }),
        fetchReportSafe("eventCount", { formattedValue: "0", value: "0", unit: "events" }),
        fetchReportSafe("topPages", { columns: [], rows: [] }),
        fetchReportSafe("sessionsBySource", { columns: [], rows: [] }),
        fetchReportSafe("newVsReturningUsers", { labels: [], datasets: [] }),
      ]);

      setActiveUsers(activeUsersData);
      setNewUsers(newUsersData);
      setAvgEngagement(avgEngagementData);
      setEventCount(eventCountData);
      setTopPages(topPagesData);
      setTrafficSources(sourcesData);
      setUserTrend(trendData);
    } catch (err) {
      console.error("Error in analytics aggregator:", err);
      setError("Failed to load Google Analytics dashboard. Please ensure credentials are properly configured.");
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilterSubmit = (e) => {
    e.preventDefault();
    fetchAnalyticsData();
  };

  // Helper to format average engagement duration seconds
  const formatSeconds = (secStr) => {
    if (!secStr) return "0s";
    const sec = parseInt(secStr, 10);
    if (isNaN(sec)) return "0s";
    const mins = Math.floor(sec / 60);
    const remainingSecs = sec % 60;
    if (mins > 0) {
      return `${mins}m ${remainingSecs}s`;
    }
    return `${sec}s`;
  };

  // Helper to format GA date strings "YYYYMMDD" to human readable "MMM DD"
  const formatDateLabel = (dateStr) => {
    if (!dateStr || dateStr.length !== 8) return dateStr;
    const year = dateStr.substring(0, 4);
    const month = parseInt(dateStr.substring(4, 6), 10) - 1;
    const day = parseInt(dateStr.substring(6, 8), 10);
    const dateObj = new Date(year, month, day);
    return dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const totalTrafficSessions = trafficSources.rows
    ? trafficSources.rows.reduce((acc, row) => acc + (parseFloat(row[1]) || 0), 0)
    : 0;

  // Render curved interactive SVG Line Chart
  const renderTrendChart = () => {
    const labels = userTrend.labels || [];
    const datasets = userTrend.datasets || [];

    if (labels.length === 0 || datasets.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500 font-semibold text-sm">
          No time series trend data available.
        </div>
      );
    }

    const width = 800;
    const height = 280;
    const paddingLeft = 50;
    const paddingRight = 20;
    const paddingTop = 25;
    const paddingBottom = 40;

    const plotWidth = width - paddingLeft - paddingRight;
    const plotHeight = height - paddingTop - paddingBottom;

    // Find max value across all datasets to scale Y-axis
    let maxVal = 0;
    datasets.forEach((dataset) => {
      const datasetMax = Math.max(...(dataset.data || [0]));
      if (datasetMax > maxVal) maxVal = datasetMax;
    });
    maxVal = Math.ceil(maxVal * 1.15) || 10;

    const totalPoints = labels.length;

    // Map data points to SVG coordinates
    const getCoordinates = (data) => {
      return data.map((val, i) => {
        const x = paddingLeft + (i * plotWidth) / Math.max(totalPoints - 1, 1);
        const y = paddingTop + plotHeight - (val / maxVal) * plotHeight;
        return { x, y, value: val };
      });
    };

    const dsCoords = datasets.map((ds) => getCoordinates(ds.data || []));

    // Helper to generate SVG smooth path
    const getLinePath = (coords) => {
      if (coords.length === 0) return "";
      return coords.reduce((acc, point, i) => {
        return i === 0 ? `M ${point.x} ${point.y}` : `${acc} L ${point.x} ${point.y}`;
      }, "");
    };

    // Helper to generate SVG closed area path for gradients
    const getAreaPath = (coords) => {
      if (coords.length === 0) return "";
      const baseLineY = paddingTop + plotHeight;
      const linePath = getLinePath(coords);
      return `${linePath} L ${coords[coords.length - 1].x} ${baseLineY} L ${coords[0].x} ${baseLineY} Z`;
    };

    // Calculate dynamic hover vertical line X coordinate
    let hoverLineX = null;
    if (hoveredTrendIndex !== null && hoveredTrendIndex >= 0 && hoveredTrendIndex < totalPoints) {
      hoverLineX = paddingLeft + (hoveredTrendIndex * plotWidth) / Math.max(totalPoints - 1, 1);
    }

    // Grid y positions
    const gridTicks = 4;
    const gridLinesY = Array.from({ length: gridTicks + 1 }, (_, i) => {
      const y = paddingTop + (i * plotHeight) / gridTicks;
      const val = Math.round(maxVal - (i * maxVal) / gridTicks);
      return { y, val };
    });

    return (
      <div className="relative w-full overflow-x-auto bg-white dark:bg-gray-800 p-6 rounded-xl shadow border border-gray-100 dark:border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="font-bold text-gray-800 dark:text-white text-base">User Acquisition Trend</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Daily new vs returning active users</p>
          </div>
          {/* Legend */}
          <div className="flex gap-4 text-xs font-semibold">
            {datasets.map((ds, idx) => (
              <div key={idx} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: ds.borderColor || colorPalette[idx] }}></span>
                <span className="text-gray-600 dark:text-gray-300">{ds.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto overflow-visible select-none">
            <defs>
              {datasets.map((ds, idx) => {
                const color = ds.borderColor || colorPalette[idx];
                return (
                  <linearGradient key={idx} id={`grad-${idx}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.4" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.0" />
                  </linearGradient>
                );
              })}
            </defs>

            {/* Horizontal Grid lines & Y labels */}
            {gridLinesY.map((grid, idx) => (
              <g key={idx}>
                <line
                  x1={paddingLeft}
                  y1={grid.y}
                  x2={width - paddingRight}
                  y2={grid.y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                  className="dark:stroke-gray-700"
                />
                <text
                  x={paddingLeft - 8}
                  y={grid.y + 4}
                  textAnchor="end"
                  fontSize="10"
                  fontWeight="600"
                  fill="#9ca3af"
                >
                  {grid.val}
                </text>
              </g>
            ))}

            {/* Vertical Hover Guide Line */}
            {hoverLineX !== null && (
              <line
                x1={hoverLineX}
                y1={paddingTop}
                x2={hoverLineX}
                y2={paddingTop + plotHeight}
                stroke="#3b82f6"
                strokeWidth="1.5"
                strokeDasharray="3 3"
              />
            )}

            {/* Render Gradients (Area) */}
            {dsCoords.map((coords, idx) => (
              <path
                key={idx}
                d={getAreaPath(coords)}
                fill={`url(#grad-${idx})`}
              />
            ))}

            {/* Render Paths (Lines) */}
            {dsCoords.map((coords, idx) => (
              <path
                key={idx}
                d={getLinePath(coords)}
                fill="none"
                stroke={datasets[idx].borderColor || colorPalette[idx]}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}

            {/* Interactive Circles / Hover Points */}
            {dsCoords.map((coords, dsIdx) => {
              if (hoveredTrendIndex === null) return null;
              const point = coords[hoveredTrendIndex];
              if (!point) return null;
              return (
                <circle
                  key={dsIdx}
                  cx={point.x}
                  cy={point.y}
                  r="5"
                  fill={datasets[dsIdx].borderColor || colorPalette[dsIdx]}
                  stroke="#ffffff"
                  strokeWidth="1.5"
                  className="shadow-sm"
                />
              );
            })}

            {/* X-axis date labels */}
            {labels.map((lbl, idx) => {
              // Draw label every 5-6 days to prevent overlapping text
              if (totalPoints > 7 && idx % Math.ceil(totalPoints / 6) !== 0 && idx !== totalPoints - 1) return null;
              const x = paddingLeft + (idx * plotWidth) / Math.max(totalPoints - 1, 1);
              return (
                <text
                  key={idx}
                  x={x}
                  y={height - 15}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="600"
                  fill="#9ca3af"
                >
                  {formatDateLabel(lbl)}
                </text>
              );
            })}

            {/* Invisible vertical trigger zones for mouse hovering */}
            {labels.map((_, idx) => {
              const rectWidth = plotWidth / Math.max(totalPoints - 1, 1);
              const x = paddingLeft + idx * rectWidth - rectWidth / 2;
              return (
                <rect
                  key={idx}
                  x={x}
                  y={paddingTop}
                  width={rectWidth}
                  height={plotHeight}
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredTrendIndex(idx)}
                  onMouseLeave={() => setHoveredTrendIndex(null)}
                />
              );
            })}
          </svg>

          {/* Floating Hover Tooltip */}
          {hoveredTrendIndex !== null && dsCoords[0]?.[hoveredTrendIndex] && (
            <div
              className="absolute z-10 bg-gray-900 text-white rounded-lg shadow-xl p-3 border border-gray-700 text-xs pointer-events-none"
              style={{
                left: `${Math.min(
                  Math.max(dsCoords[0][hoveredTrendIndex].x - 60, 20),
                  width - 150
                )}px`,
                top: `${Math.max(dsCoords[0][hoveredTrendIndex].y - 95, 10)}px`,
              }}
            >
              <div className="font-bold border-b border-gray-700 pb-1 mb-1.5 text-gray-300">
                {formatDateLabel(labels[hoveredTrendIndex])}
              </div>
              <div className="space-y-1 font-semibold">
                {datasets.map((ds, idx) => (
                  <div key={idx} className="flex justify-between items-center gap-4">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: ds.borderColor || colorPalette[idx] }}></span>
                      {ds.label}:
                    </span>
                    <span className="font-extrabold text-white">
                      {ds.data[hoveredTrendIndex]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render circular SVG Donut Chart for traffic distribution
  const renderDonutChart = () => {
    if (!trafficSources.rows || trafficSources.rows.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500 dark:text-gray-300 text-xs font-semibold bg-white dark:bg-gray-800">
          No channel traffic records available.
        </div>
      );
    }

    const radius = 50;
    const strokeWidth = 14;
    const circumference = 2 * Math.PI * radius; // 314.159

    let accumulatedPercentage = 0;

    const parsedSlices = trafficSources.rows.map((row, idx) => {
      const name = row[0] || "Direct / None";
      const value = parseFloat(row[1]) || 0;
      const percentage = totalTrafficSessions > 0 ? (value / totalTrafficSessions) * 100 : 0;
      const strokeLength = (percentage / 100) * circumference;
      const rotation = (accumulatedPercentage / 100) * 360 - 90; // Start at 12 o'clock

      accumulatedPercentage += percentage;

      return {
        name,
        value,
        percentage: Math.round(percentage * 10) / 10,
        strokeDasharray: `${strokeLength} ${circumference}`,
        rotation,
        color: colorPalette[idx % colorPalette.length],
      };
    });

    return (
      <div className="flex flex-col sm:flex-row items-center justify-around gap-6">
        {/* SVG Donut */}
        <div className="relative w-40 h-40">
          <svg viewBox="0 0 120 120" className="w-full h-full transform -rotate-90">
            {/* Background base circle */}
            <circle cx="60" cy="60" r={radius} fill="transparent" stroke="#f3f4f6" strokeWidth={strokeWidth} className="dark:stroke-gray-700" />
            
            {/* Colored segment circles */}
            {parsedSlices.map((slice, idx) => (
              <circle
                key={idx}
                cx="60"
                cy="60"
                r={radius}
                fill="transparent"
                stroke={slice.color}
                strokeWidth={strokeWidth}
                strokeDasharray={slice.strokeDasharray}
                strokeDashoffset="0"
                transform={`rotate(${slice.rotation + 90} 60 60)`}
                className="transition-all duration-300 hover:stroke-[16px] cursor-pointer"
                onMouseEnter={() => setHoveredDonutIndex(idx)}
                onMouseLeave={() => setHoveredDonutIndex(null)}
              />
            ))}
          </svg>

          {/* Center text overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none select-none text-center">
            {hoveredDonutIndex !== null ? (
              <>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">
                  {parsedSlices[hoveredDonutIndex].name.split(" ")[0]}
                </span>
                <span className="text-base font-black text-gray-800 dark:text-white mt-0.5">
                  {parsedSlices[hoveredDonutIndex].percentage}%
                </span>
              </>
            ) : (
              <>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">
                  Total
                </span>
                <span className="text-lg font-black text-gray-800 dark:text-white mt-0.5">
                  {totalTrafficSessions}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-3 w-full">
          {parsedSlices.map((slice, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between text-xs p-1.5 rounded transition ${
                hoveredDonutIndex === idx
                  ? "bg-gray-155 dark:bg-gray-700 font-bold"
                  : ""
              }`}
              onMouseEnter={() => setHoveredDonutIndex(idx)}
              onMouseLeave={() => setHoveredDonutIndex(null)}
            >
              <div className="flex items-center gap-2 truncate max-w-[170px]">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: slice.color }}></span>
                <span className="text-gray-700 dark:text-gray-300 truncate capitalize">
                  {slice.name}
                </span>
              </div>
              <span className="text-gray-500 dark:text-gray-400 font-bold ml-4">
                {slice.value} ({slice.percentage}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        @media print {
          /* Hide non-report layout elements */
          aside,
          footer,
          .print\\:hidden,
          form,
          button,
          .TopHeader {
            display: none !important;
          }
          
          /* Reset container widths for printing */
          body, 
          .flex-1, 
          main, 
          .container,
          .mx-auto {
            background: #ffffff !important;
            color: #000000 !important;
            padding: 0 !important;
            margin: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            box-shadow: none !important;
          }
          
          /* Adjust grids for PDF output layout */
          .grid {
            display: grid !important;
            grid-template-columns: repeat(12, minmax(0, 1fr)) !important;
            gap: 1.5rem !important;
          }
          
          .grid-cols-1,
          .sm\\:grid-cols-2,
          .lg\\:grid-cols-4 {
            grid-template-columns: repeat(12, minmax(0, 1fr)) !important;
          }
          
          /* Metric cards split 2x2 on page */
          .sm\\:grid-cols-2 > div,
          .lg\\:grid-cols-4 > div {
            grid-column: span 6 !important;
            background: #ffffff !important;
            color: #000000 !important;
            border: 1px solid #e5e7eb !important;
            box-shadow: none !important;
            margin-bottom: 1rem !important;
          }
          
          /* Tables grid layout */
          .lg\\:col-span-8 {
            grid-column: span 8 !important;
            background: #ffffff !important;
            border: 1px solid #e5e7eb !important;
          }
          
          .lg\\:col-span-4 {
            grid-column: span 4 !important;
            background: #ffffff !important;
            border: 1px solid #e5e7eb !important;
          }
          
          /* Force standard dark text for premium print readability */
          h1, h2, h3, p, td, th, span {
            color: #000000 !important;
          }
          
          .text-white, .dark\\:text-white {
            color: #000000 !important;
          }
          
          .bg-white, .dark\\:bg-gray-800 {
            background-color: #ffffff !important;
          }
          
          /* Ensure session progress bar prints with color */
          .bg-blue-500 {
            background-color: #3b82f6 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>

      <TopHeader />
      <div className="container mx-auto px-4 py-6 font-sans">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900">
              Google Analytics Dashboard
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Live engagement, user acquisition, and popular pages from Google Analytics 4.
            </p>
          </div>

          {/* Date Picker & Export Actions Form */}
          <div className="flex items-center flex-wrap gap-3 print:hidden">
            <form
              onSubmit={handleDateFilterSubmit}
              className="flex items-center flex-wrap gap-3 bg-white dark:bg-gray-800 p-2.5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase">
                  From:
                </span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase">
                  To:
                </span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-3.5 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold rounded transition"
              >
                Apply
              </button>
            </form>

            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm transition-all hover:shadow"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              Export PDF
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-amber-50 dark:bg-amber-950/20 border-l-4 border-amber-500 p-5 rounded-lg mb-8 shadow-sm print:hidden">
            <div className="flex items-start gap-3">
              <div className="text-amber-500 mt-0.5">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">
                  Analytics Offline or Unconfigured
                </h3>
                <p className="text-xs text-amber-700 dark:text-amber-400/90 mt-1 max-w-2xl leading-relaxed">
                  {error}
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <button
                    onClick={fetchAnalyticsData}
                    className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded shadow transition"
                  >
                    Retry Connection
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <img src={loader} alt="Loading Analytics" className="w-16 h-16" />
          </div>
        ) : !error ? (
          <div className="space-y-8">
            {/* Metric Cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Active Users */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border-b-4 border-blue-500 hover:shadow-md transition">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs font-bold text-gray-400 dark:text-gray-400 uppercase tracking-wider mb-1">
                      Active Users
                    </p>
                    <h3 className="text-2xl font-black text-gray-800 dark:text-white">
                      {activeUsers?.formattedValue || "0"}
                    </h3>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 capitalize">
                  Unique visitors on the site
                </p>
              </div>

              {/* New Users */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border-b-4 border-green-500 hover:shadow-md transition">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs font-bold text-gray-400 dark:text-gray-400 uppercase tracking-wider mb-1">
                      New Users
                    </p>
                    <h3 className="text-2xl font-black text-gray-800 dark:text-white">
                      {newUsers?.formattedValue || "0"}
                    </h3>
                  </div>
                  <div className="p-3 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                  First-time visitor counts
                </p>
              </div>

              {/* Average Engagement */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border-b-4 border-purple-500 hover:shadow-md transition">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs font-bold text-gray-400 dark:text-gray-400 uppercase tracking-wider mb-1">
                      Avg Engagement
                    </p>
                    <h3 className="text-2xl font-black text-gray-800 dark:text-white">
                      {avgEngagement ? formatSeconds(avgEngagement.value) : "0s"}
                    </h3>
                  </div>
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                  Duration active per session
                </p>
              </div>

              {/* Event Count */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border-b-4 border-amber-500 hover:shadow-md transition">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs font-bold text-gray-400 dark:text-gray-400 uppercase tracking-wider mb-1">
                      Event Count
                    </p>
                    <h3 className="text-2xl font-black text-gray-800 dark:text-white">
                      {eventCount?.formattedValue || "0"}
                    </h3>
                  </div>
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                  Clicks, views, actions total
                </p>
              </div>
            </div>

            {/* Interactive Trend Chart */}
            {renderTrendChart()}

            {/* Tables Area */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Top Pages (8 cols) */}
              <div className="lg:col-span-8 bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
                <div className="px-6 py-5 border-b border-gray-150 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <h3 className="font-bold text-gray-800 dark:text-white text-base">Top Viewed Pages</h3>
                  <p className="text-xs text-gray-505 dark:text-gray-400 mt-0.5">Most viewed screen titles on Balarch</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-300">
                      <tr>
                        <th scope="col" className="px-6 py-4">Page Title</th>
                        <th scope="col" className="px-6 py-4 text-center">Pageviews</th>
                        <th scope="col" className="px-6 py-4 text-center">Active Users</th>
                        <th scope="col" className="px-6 py-4 text-center">Event Count</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {topPages.rows && topPages.rows.length > 0 ? (
                        topPages.rows.map((row, idx) => (
                          <tr
                            key={idx}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-white dark:bg-gray-800 transition"
                          >
                            <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white max-w-sm truncate text-xs">
                              {row[0] || "Home"}
                            </td>
                            <td className="px-6 py-4 text-center text-gray-700 dark:text-gray-300 text-xs">
                              {parseFloat(row[1]).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-center text-gray-700 dark:text-gray-300 text-xs">
                              {parseFloat(row[2]).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-center text-gray-700 dark:text-gray-300 text-xs">
                              {parseFloat(row[3]).toLocaleString()}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-12 text-gray-500 dark:text-gray-300 text-xs font-semibold bg-white dark:bg-gray-800">
                            No pageview records available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Traffic Sources - SVG Donut (4 cols) */}
              <div className="lg:col-span-4 bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
                <div className="px-6 py-5 border-b border-gray-150 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <h3 className="font-bold text-gray-800 dark:text-white text-base">Traffic Channels</h3>
                  <p className="text-xs text-gray-505 dark:text-gray-400 mt-0.5">Sessions by source channel medium</p>
                </div>
                <div className="p-6 bg-white dark:bg-gray-800">
                  {renderDonutChart()}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default AdminAnalytics;
